import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Paper,
  Button,
  Stack,
  Divider,
} from "@mui/material";
import { Sunrise, Sunset, Pencil } from "lucide-react";
import { DeceasedPerson } from "../../../../../bookings/types/edit-booking";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../store/Index";
import ObituaryEditor from "../../../../../../components/common/ObituaryEditor";

interface ObituaryTabProps {
  deceasedData: DeceasedPerson | null;
  streamData?: {
    booking: {
      deceased: Array<{
        id: number;
        person_image?: string;
      }>;
    };
  } | null;
  isLoadingDeceased: boolean;
  isAdmin: boolean;
  accentColor?: string;
  /** "standalone" (default) renders the full photo-card layout used by the
   * regular funeral templates. "embedded" strips the outer Paper/heading so
   * a parent (e.g. FuneralHomeTemplate's unified card) can supply its own
   * chrome — content and behaviour are otherwise identical. */
  variant?: "standalone" | "embedded";
  /** Only used when variant="embedded": lets the parent control the edit
   * toggle (e.g. to render the pencil button in its own shared header). */
  isEditing?: boolean;
  onEditingChange?: (editing: boolean) => void;
}

export default function ObituaryTab({
  deceasedData,
  streamData,
  isLoadingDeceased,
  isAdmin,
  accentColor,
  variant = "standalone",
  isEditing: controlledIsEditing,
  onEditingChange,
}: ObituaryTabProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const [currentObituary, setCurrentObituary] = useState("");
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const isEditingObituary = controlledIsEditing ?? internalIsEditing;
  const setIsEditingObituary = onEditingChange ?? setInternalIsEditing;

  useEffect(() => {
    setCurrentObituary(deceasedData?.orbituary || "");
  }, [deceasedData?.orbituary]);

  const hasObituary = useMemo(() => {
    return Boolean(
      currentObituary && currentObituary.replace(/<[^>]*>/g, "").trim(),
    );
  }, [currentObituary]);

  const shouldShowEditor = isAdmin && (!hasObituary || isEditingObituary);
  const isEmbedded = variant === "embedded";

  if (isLoadingDeceased) {
    return (
      <Typography variant="body1" sx={{ textAlign: "center", mt: 3 }}>
        Loading obituary...
      </Typography>
    );
  }

  if (!deceasedData) {
    return (
      <Typography
        variant="body1"
        sx={{
          textAlign: "center",
          mt: 3,
          color: reduxTheme.grey_colors[500],
        }}
      >
        No obituary data available.
      </Typography>
    );
  }

  const brand = accentColor || reduxTheme.primary_colors[400];
  const photoSrc =
    deceasedData?.person_image ||
    streamData?.booking.deceased[0]?.person_image ||
    "";
  const initials = [deceasedData?.first_name, deceasedData?.surname]
    .filter(Boolean)
    .map((n) => n!.charAt(0).toUpperCase())
    .join("");

  const formatDate = (value?: string) =>
    value
      ? new Date(value).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : "N/A";

  const obituaryBody = (
    <Box
      sx={{
        color: isEmbedded ? "text.primary" : reduxTheme.grey_colors[100],
        "& p": {
          color: isEmbedded ? "text.primary" : reduxTheme.grey_colors[100],
          lineHeight: 1.7,
          fontSize: { xs: "0.875rem", md: "1rem" },
          m: 0,
        },
        "& p + p": { mt: 1.5 },
        "& h1, & h2, & h3, & h4, & h5, & h6": {
          color: isEmbedded ? "text.primary" : reduxTheme.grey_colors[100],
        },
        "& a": {
          color: isEmbedded ? brand : reduxTheme.secondary_colors[300],
        },
        ...(!hasObituary && {
          "& p": { fontStyle: "italic", color: "text.secondary" },
        }),
        ...(!isEmbedded && {
          p: { xs: 2, md: 3 },
          backgroundColor: reduxTheme.primary_colors[800],
          borderLeft: "4px solid",
          borderColor: brand,
          borderRadius: "4px 12px 12px 4px",
        }),
      }}
    >
      <div
        dangerouslySetInnerHTML={{
          __html: currentObituary?.trim()
            ? currentObituary
            : "<p>No obituary available yet.</p>",
        }}
      />
    </Box>
  );

  if (isEmbedded) {
    return (
      <Box>
        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
          <Avatar
            src={photoSrc}
            sx={{
              width: 56,
              height: 56,
              flexShrink: 0,
              bgcolor: `${brand}1f`,
              color: brand,
              border: "2px solid",
              borderColor: brand,
              fontWeight: 700,
            }}
          >
            {!photoSrc && initials}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontStyle: "italic",
                fontFamily: '"Dancing Script", "Freehand", cursive',
                fontSize: "1rem",
                color: "text.secondary",
              }}
            >
              In loving memory of
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
              {[deceasedData?.first_name, deceasedData?.surname]
                .filter(Boolean)
                .join(" ") || "N/A"}
            </Typography>
            <Stack direction="row" spacing={3} sx={{ mt: 0.5 }}>
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ display: "block", letterSpacing: 0.5, fontSize: "0.65rem" }}
                  color="text.secondary"
                >
                  SUNRISE
                </Typography>
                <Typography sx={{ fontSize: "0.8rem" }}>
                  {formatDate(deceasedData?.date_of_birth)}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ display: "block", letterSpacing: 0.5, fontSize: "0.65rem" }}
                  color="text.secondary"
                >
                  SUNSET
                </Typography>
                <Typography sx={{ fontSize: "0.8rem" }}>
                  {formatDate(deceasedData?.date_of_death)}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Stack>

        {shouldShowEditor ? (
          <ObituaryEditor
            obituary={currentObituary}
            deceasedId={deceasedData.id}
            onSaveSuccess={(savedHtml) => {
              setCurrentObituary(savedHtml);
              setIsEditingObituary(false);
            }}
            onCancel={
              hasObituary
                ? () => {
                    setIsEditingObituary(false);
                  }
                : undefined
            }
          />
        ) : (
          obituaryBody
        )}
      </Box>
    );
  }

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, boxShadow: 0, borderRadius: "20px" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 3, md: 4 }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: 220 },
            flexShrink: 0,
            display: "flex",
            justifyContent: { xs: "center", md: "flex-start" },
          }}
        >
          <Avatar
            src={photoSrc}
            variant="rounded"
            sx={{
              width: { xs: 160, md: "100%" },
              height: { xs: 160, md: 220 },
              borderRadius: 3,
              border: "3px solid",
              borderColor: brand,
              boxShadow: `0 8px 24px ${reduxTheme.primary_colors[800]}`,
            }}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={2}
            sx={{ mb: 2.5 }}
          >
            <Stack
              direction="row"
              divider={<Divider orientation="vertical" flexItem />}
              spacing={2}
              justifyContent={{ xs: "center", sm: "flex-start" }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Sunrise size={20} color={brand} />
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      display: "block",
                      letterSpacing: 0.5,
                      fontSize: "0.65rem",
                      color: brand,
                    }}
                  >
                    SUNRISE
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 500 }}>
                    {formatDate(deceasedData?.date_of_birth)}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <Sunset size={20} color={brand} />
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      display: "block",
                      letterSpacing: 0.5,
                      fontSize: "0.65rem",
                      color: brand,
                    }}
                  >
                    SUNSET
                  </Typography>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 500 }}>
                    {formatDate(deceasedData?.date_of_death)}
                  </Typography>
                </Box>
              </Stack>
            </Stack>

            {isAdmin && !shouldShowEditor && (
              <Button
                variant="contained"
                startIcon={<Pencil size={16} />}
                fullWidth={false}
                sx={{
                  backgroundColor: brand,
                  alignSelf: { xs: "center", sm: "auto" },
                  "&:hover": { backgroundColor: brand, filter: "brightness(0.9)" },
                }}
                onClick={() => setIsEditingObituary(true)}
              >
                Edit Obituary
              </Button>
            )}
          </Stack>

          {shouldShowEditor ? (
            <ObituaryEditor
              obituary={currentObituary}
              deceasedId={deceasedData.id}
              onSaveSuccess={(savedHtml) => {
                setCurrentObituary(savedHtml);
                setIsEditingObituary(false);
              }}
              onCancel={
                hasObituary
                  ? () => {
                      setIsEditingObituary(false);
                    }
                  : undefined
              }
            />
          ) : (
            obituaryBody
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
